import React, { useEffect, useRef } from 'react';

/**
 * Animated SVG visualization for the hero section.
 * Shows interconnected nodes forming an orchestration pipeline —
 * abstract enough to not reveal product details, concrete enough
 * to suggest structured flow from chaos to order.
 */
function HeroVisual() {
    const svgRef = useRef(null);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        // Animate nodes in sequence
        const nodes = svg.querySelectorAll('.hero-node');
        const paths = svg.querySelectorAll('.hero-path');

        nodes.forEach((node, i) => {
            node.style.animationDelay = `${i * 0.15}s`;
        });

        paths.forEach((path, i) => {
            path.style.animationDelay = `${i * 0.2 + 0.3}s`;
        });
    }, []);

    return (
        <div className="hero-visual-container">
            <svg
                ref={svgRef}
                viewBox="0 0 600 200"
                className="hero-visual-svg"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <linearGradient id="pathGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(42, 82, 152, 0.6)" />
                        <stop offset="100%" stopColor="rgba(120, 170, 240, 0.4)" />
                    </linearGradient>
                    <linearGradient id="nodeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(42, 82, 152, 0.8)" />
                        <stop offset="100%" stopColor="rgba(120, 170, 240, 0.6)" />
                    </linearGradient>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Scattered inputs (left side - "chaos") */}
                <circle className="hero-node scatter-node" cx="40" cy="40" r="5" fill="rgba(255,255,255,0.15)" />
                <circle className="hero-node scatter-node" cx="60" cy="120" r="4" fill="rgba(255,255,255,0.12)" />
                <circle className="hero-node scatter-node" cx="30" cy="160" r="6" fill="rgba(255,255,255,0.1)" />
                <circle className="hero-node scatter-node" cx="80" cy="70" r="3" fill="rgba(255,255,255,0.15)" />
                <circle className="hero-node scatter-node" cx="50" cy="90" r="4" fill="rgba(255,255,255,0.12)" />

                {/* Convergence paths to first gate */}
                <path className="hero-path" d="M 45 42 Q 90 60 120 80" stroke="url(#pathGrad)" strokeWidth="1" fill="none" opacity="0.4" />
                <path className="hero-path" d="M 64 118 Q 90 100 120 85" stroke="url(#pathGrad)" strokeWidth="1" fill="none" opacity="0.4" />
                <path className="hero-path" d="M 36 158 Q 80 130 120 90" stroke="url(#pathGrad)" strokeWidth="1" fill="none" opacity="0.3" />
                <path className="hero-path" d="M 83 72 Q 100 75 120 82" stroke="url(#pathGrad)" strokeWidth="1" fill="none" opacity="0.4" />

                {/* Gate 1: Governance */}
                <rect className="hero-node gate-node" x="120" y="65" width="90" height="40" rx="8" fill="url(#nodeGrad)" filter="url(#glow)" opacity="0.75" />
                <text x="165" y="90" textAnchor="middle" fill="white" fontSize="11" fontWeight="600" letterSpacing="0.05em" opacity="0.95">GOVERN</text>

                {/* Path: Gate 1 → Gate 2 */}
                <path className="hero-path main-path" d="M 210 85 Q 227 85 245 85" stroke="url(#pathGrad)" strokeWidth="2" fill="none" opacity="0.6" />

                {/* Flowing dots on main path */}
                <circle className="flow-dot" r="2.5" fill="rgba(120, 170, 240, 0.8)">
                    <animateMotion dur="3s" repeatCount="indefinite" path="M 210 85 Q 227 85 245 85" />
                </circle>

                {/* Gate 2: Alignment */}
                <rect className="hero-node gate-node" x="245" y="65" width="90" height="40" rx="8" fill="url(#nodeGrad)" filter="url(#glow)" opacity="0.75" />
                <text x="290" y="90" textAnchor="middle" fill="white" fontSize="11" fontWeight="600" letterSpacing="0.05em" opacity="0.95">ALIGN</text>

                {/* Path: Gate 2 → Gate 3 */}
                <path className="hero-path main-path" d="M 335 85 Q 352 85 370 85" stroke="url(#pathGrad)" strokeWidth="2" fill="none" opacity="0.6" />

                <circle className="flow-dot" r="2.5" fill="rgba(120, 170, 240, 0.8)">
                    <animateMotion dur="3s" repeatCount="indefinite" begin="0.8s" path="M 335 85 Q 352 85 370 85" />
                </circle>

                {/* Gate 3: Orchestration */}
                <rect className="hero-node gate-node" x="370" y="65" width="110" height="40" rx="8" fill="url(#nodeGrad)" filter="url(#glow)" opacity="0.75" />
                <text x="425" y="90" textAnchor="middle" fill="white" fontSize="11" fontWeight="600" letterSpacing="0.05em" opacity="0.95">ORCHESTRATE</text>

                {/* Path: Gate 3 → Output */}
                <path className="hero-path main-path" d="M 480 85 Q 495 85 510 85" stroke="url(#pathGrad)" strokeWidth="2" fill="none" opacity="0.6" />

                <circle className="flow-dot" r="2.5" fill="rgba(120, 170, 240, 0.8)">
                    <animateMotion dur="3s" repeatCount="indefinite" begin="1.6s" path="M 480 85 Q 495 85 510 85" />
                </circle>

                {/* Output: Structured result (right side - "order") */}
                <rect className="hero-node output-node" x="510" y="55" width="80" height="60" rx="10" fill="rgba(42, 82, 152, 0.3)" stroke="rgba(120, 170, 240, 0.3)" strokeWidth="1.5" />
                <line x1="525" y1="72" x2="575" y2="72" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
                <line x1="525" y1="82" x2="565" y2="82" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                <line x1="525" y1="92" x2="580" y2="92" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                <line x1="525" y1="102" x2="555" y2="102" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

                {/* Decision ledger lines (subtle, below main flow) */}
                <path className="hero-path ledger-path" d="M 165 105 L 165 140 L 425 140 L 425 105" stroke="rgba(255,255,255,0.08)" strokeWidth="1" fill="none" strokeDasharray="4 4" />
            </svg>
        </div>
    );
}

export default HeroVisual;
